/**
 * @fileoverview The `/chat-streaming` handshake contract, client side.
 *
 * The server identifies a `/chat-streaming` connection from `handshake.auth`
 * and accepts exactly two identities. Everything else is refused before any
 * event fires:
 *
 * 1. **Authenticated dashboard user** — `token` is a Clerk JWT, the same
 *    string the HTTP layer sends as `Authorization: Bearer <token>`. The
 *    server verifies it against Clerk's JWKS and derives the user id from
 *    `sub` and the organization id from `org_id`.
 * 2. **Anonymous embed visitor** — `agentId` is the public agent id of the
 *    EMBED deployment, the same value the HTTP layer sends as the
 *    `x-agent-id` header. The server resolves the deployment and takes the
 *    organization id from that row. There is no user in this case.
 *
 * 🚨 **`organizationId` is never sent.** A client-supplied organization id was
 * the cross-tenant leak this contract exists to close: the server took the
 * caller's word for which tenant it belonged to. The organization is now
 * derived server-side from a verified token or a looked-up deployment, and
 * anything the client puts in the handshake beyond the two fields above is
 * ignored.
 *
 * 🚨 **The literal string `"anonymous"` is not a token.** It used to be sent
 * as a placeholder whenever no JWT was to hand. It is now refused like any
 * other unverifiable value.
 */

/**
 * The handshake payload. `token` and `agentId` are the only authorization
 * inputs, and exactly one of them is sent.
 */
export interface ChatStreamingHandshakeAuth {
  /** Clerk JWT. Present for a signed-in dashboard user, absent otherwise. */
  token?: string
  /** Public agent id of the EMBED deployment. Present for an embed visitor. */
  agentId?: string
  /** Display name for presence. Cosmetic, never authorization. */
  userName?: string
  /** Avatar URL for presence. Cosmetic, never authorization. */
  avatarUrl?: string
}

/**
 * The exact `Error.message` the server's handshake middleware rejects with
 * (`next(new Error("UNAUTHORIZED"))`), which socket.io surfaces on the client
 * as a `connect_error`.
 */
export const CHAT_STREAMING_UNAUTHORIZED = "UNAUTHORIZED" as const

/**
 * User-facing copy for a refused handshake. Deliberately distinct from the
 * transport-failure copy: a retry cannot fix this one, so the instruction is
 * to reload rather than to wait.
 */
export const CHAT_STREAMING_UNAUTHORIZED_MESSAGE =
  "Chat is not authorized for this session. Please refresh the page."

/**
 * True when a `connect_error` is the server refusing the handshake, rather
 * than a transport failure.
 *
 * The two need opposite handling, and telling them apart is the whole job of
 * this function. Measured against socket.io-client 4.8.1
 * (`build/cjs/socket.js`, `PacketType.CONNECT_ERROR`):
 *
 * - **Transport failure** — the manager retries on its own schedule and
 *   usually wins, and reports giving up as `reconnect_failed`. Surfacing an
 *   error per attempt would only flash a message nobody can act on.
 * - **Refused handshake** — the client calls `destroy()` on the socket and
 *   emits `connect_error`. It does NOT retry, whatever `reconnectionAttempts`
 *   says, and `reconnect_failed` therefore never fires. So an empty
 *   `connect_error` handler leaves the socket permanently dead with
 *   `isConnected: false`, `error: null` and no path that will ever set one —
 *   a chat that has silently stopped working and looks merely idle.
 *
 * Transport failures carry socket.io's own messages (`"timeout"`,
 * `"xhr poll error"`, `"websocket error"`, …), none of which collide with the
 * rejection literal. `data.code` is also read so the server can attach a
 * structured reason later without this guard changing.
 */
/** The identity fields the server publishes on `connection:ready`. */
export interface ChatStreamingReadyIdentity {
  /** Clerk `sub` for a signed-in user; the empty string for an embed visitor. */
  userId?: string
  /** Verified organization. Present for BOTH identities — see below. */
  organizationId?: string
}

/**
 * Which rooms this connection may ask the server for, decided ONCE.
 *
 * 🚨 THE ORGANIZATION ID IS NOT THE DISCRIMINATOR, AND ASSUMING IT WAS IS THE
 * BUG THIS FUNCTION EXISTS TO PREVENT. An anonymous embed visitor now carries a
 * perfectly real, server-verified `organizationId` — resolved from the EMBED
 * deployment — so `if (data.organizationId)` is TRUE for both identities. The
 * only thing that separates them is whether a user exists.
 *
 * The org rooms (`org:<id>:inbox` / `:playground`) carry `chat:created`,
 * `chat:updated` and `chat:deleted` for EVERY chat in the tenant. A visitor on
 * a customer's website belongs in exactly one of them: none. Being pushed the
 * existence and title of every other visitor's conversation, unasked, is
 * strictly worse than NEX-3518 — that is a pull requiring a known chat id, this
 * would be a broadcast. So the server refusing is correct, and a client asking
 * anyway buys a guaranteed-refused round trip plus an unactionable server warn
 * on every connect AND every reconnect.
 *
 * Both room requests read this one function, so the two cannot disagree again.
 * They already did: `user:join` was guarded and `organization:join`, one branch
 * below it, was not.
 */
export function roomsToRequestOnReady(identity: ChatStreamingReadyIdentity): {
  user: boolean
  organization: boolean
} {
  const isSignedInUser =
    typeof identity.userId === "string" &&
    identity.userId.length > 0 &&
    identity.userId !== "anonymous"

  return {
    user: isSignedInUser,
    organization: isSignedInUser && typeof identity.organizationId === "string"
  }
}

export function isChatStreamingHandshakeRejected(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false
  }
  if (error.message === CHAT_STREAMING_UNAUTHORIZED) {
    return true
  }
  const { data } = error as Error & { data?: unknown }
  return (
    typeof data === "object" &&
    data !== null &&
    "code" in data &&
    (data as { code?: unknown }).code === CHAT_STREAMING_UNAUTHORIZED
  )
}
